/**
 * useAdaptiveSuggestions — Hook that computes and manages adaptive training suggestions.
 *
 * Wraps the pure rule engine with dismiss persistence (localStorage, per-day).
 * Consumers pass the AdaptiveContext built from already-fetched dashboard data.
 *
 * @since Adaptive Suggestion Layer v1
 */

import { useState, useCallback, useMemo } from "react";
import {
  computeAdaptiveSuggestions,
  type AdaptiveContext,
  type AdaptiveSuggestion,
} from "@/lib/adaptiveSuggestionEngine";

const TODAY_KEY = new Date().toISOString().split("T")[0];
const STORAGE_PREFIX = "adaptive_dismissed:";

function isDismissed(id: string): boolean {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${id}:${TODAY_KEY}`) === "1";
  } catch {
    return false;
  }
}

function markDismissed(id: string): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${id}:${TODAY_KEY}`, "1");
  } catch {
    // localStorage unavailable — suggestion will reappear, acceptable
  }
}

export function useAdaptiveSuggestions(ctx: AdaptiveContext) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const suggestions: AdaptiveSuggestion[] = useMemo(() => {
    const all = computeAdaptiveSuggestions(ctx);
    return all.filter((s) => !isDismissed(s.id) && !dismissed.has(s.id));
  }, [ctx, dismissed]);

  const dismiss = useCallback((id: string) => {
    markDismissed(id);
    setDismissed((prev) => new Set(prev).add(id));
  }, []);

  return { suggestions, dismiss };
}
